<script setup lang="ts">
import { Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useResponsive from '@/composables/useResponsive.js';
import useRouting from '@/composables/useRouting.js';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

import MobileProductFilters from '../mode-list-filters/MobileProductFilters.vue';
import MobileSearchFilters from '../mode-list-filters/MobileSearchFilters.vue';
import TheMobileSlider from './TheMobileSlider.vue';

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale, globalConfiguration } = storeToRefs(globalConfigurationStore);

const metricsStore = useMetricsStore();
const { themes } = storeToRefs(metricsStore);

const productsStore = useProductsStore();

const filtersStore = useFiltersStore();
const { datesAsStrings, filters } = storeToRefs(filtersStore);

const { isSmallerThan350Px } = useResponsive();
const { defaultRoute, redirectOrFetch } = useRouting();
const isSearchOpen = ref(false);
const isFiltersOpen = ref(false);
const filtersResetKey = ref(0);

const activeTheme = computed(() => filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes')?.selection?.[0]) ?? undefined;
const activeThemeLabel = computed(() => {
  const theme = themes.value.find(it => it.id === activeTheme.value);
  return theme?.translatedName.find((it: any) => it.lang === currentLocale.value)?.value ?? theme?.name;
});

const searchButtonLabel = computed(() => isSmallerThan350Px.value ? t('search.small_default') : t('search.default'));

function handleSearchFilters() {
  isSearchOpen.value = false;
  filtersStore.saveToStorage(filters.value);

  redirectOrFetch();

  const anchorOnGridComponent = document.querySelector('yb-list');
  if (anchorOnGridComponent && window.innerWidth < 1024)
    anchorOnGridComponent.scrollIntoView({ behavior: 'smooth' });
}

function handleProductsFilter() {
  isFiltersOpen.value = false;
  filtersStore.saveToStorage(filters.value);
  if (globalConfiguration.value && window.location.href === globalConfiguration.value.homeUrl) {
    if (defaultRoute.value) {
      window.location.href = defaultRoute.value.url;
    }
  }
  else {
    productsStore.fetchProductsList(filters.value);
  }
  const anchorOnGridComponent = document.querySelector('yb-list');
  if (anchorOnGridComponent && window.innerWidth < 1024)
    anchorOnGridComponent.scrollIntoView({ behavior: 'smooth' });
}

function resetSearchFilters() {
  filtersStore.addSearchingFilter('');
  const datesFilter = filters.value.filters.findIndex(it => it.filterProperties.type === 'date_range');
  if (datesFilter && datesFilter > -1) {
    datesAsStrings.value = null;
    filters.value.filters.splice(datesFilter, 1);
  }
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyThemes');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyTypes');
  filters.value.metrics = filters.value.metrics.filter(it => it.type !== 'crossedTaxonomyCount');
  filtersResetKey.value++;
  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}

function resetProductFilters() {
  // detele PriceRange filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'priceFrom');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'priceFrom');
  // delete Duration filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'sessionDuration');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'sessionDuration');
  // delete TimeRange filter
  filters.value.filters = filters.value.filters.filter(it => it.filterProperties.type !== 'timeRange');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'timeRange');
  // delete Places filter
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'taxonomyPlaces');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties?.type !== 'product_enrichment_taxonomy');
  // Force re-render of filter components to reset their internal state
  filtersResetKey.value++;
  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}
</script>

<template>
  <div class="mobile-filters-wrapper">
    <button
      :aria-haspopup="true"
      aria-controls="search-dialog"
      class="search-button"
      @click="isSearchOpen = !isSearchOpen"
    >
      <div class="search-button-content">
        <Search :size="22" color="var(--yb-color-black)" aria-hidden="true" />
        <div v-if="activeThemeLabel || datesAsStrings" class="search-button-results">
          <p class="theme">
            {{ activeThemeLabel ?? searchButtonLabel }}
          </p>
          <p v-if="globalConfiguration?.options?.hasCalendar" class="dates">
            {{ datesAsStrings ?? t('search.dates') }}
          </p>
        </div>
        <p v-else class="search-label">
          {{ searchButtonLabel }}
        </p>
      </div>
    </button>
    <button
      :aria-haspopup="true"
      aria-controls="filters-dialog"
      aria-label="ouvrir les filtres de produits"
      class="filters-button"
      @click="isFiltersOpen = !isFiltersOpen"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" aria-hidden="true">
        <path stroke="var(--yb-color-black)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.786 7.275h-9.354m3.118 10.393H5.197m9.353 0a3.118 3.118 0 1 0 6.236 0 3.118 3.118 0 0 0-6.236 0M10.393 7.275a3.118 3.118 0 1 1-6.236 0 3.118 3.118 0 0 1 6.236 0" />
      </svg>
    </button>
  </div>
  <TheMobileSlider
    :is-open="isSearchOpen" :title="t('search.label')" anchor="top" :footer="true"
    @validation="handleSearchFilters" @close="isSearchOpen = false" @reset="resetSearchFilters"
  >
    <MobileSearchFilters :key="filtersResetKey" @submit="handleSearchFilters" />
  </TheMobileSlider>
  <TheMobileSlider
    :is-open="isFiltersOpen" :title="t('menu.filters')" anchor="top" :footer="true"
    @close="isFiltersOpen = false" @validation="handleProductsFilter" @reset="resetProductFilters"
  >
    <MobileProductFilters :key="filtersResetKey" />
  </TheMobileSlider>
</template>

<style lang="css" scoped>
.mobile-filters-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100vw - var(--yb-spacing-sm));
  gap: var(--yb-spacing-sm);
  margin-bottom: 0.5rem;

  .search-button,
  .filters-button {
    all: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--yb-color-white);
    color: var(--yb-color-black);
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: var(--yb-radius-sm);
    height: 54px;
  }

  .filters-button {
    aspect-ratio: 1;
  }

  .search-button {
    flex-grow: 1;
    justify-content: flex-start;
    padding-left: var(--yb-spacing-sm);
  }

  .search-button-content {
    font-family: var(--yb-default-font, sans-serif);
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-xs);
    color: var(--yb-color-black);

    .search-button-results {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .theme {
        font-weight: var(--yb-text-bold);
        font-size: var(--yb-text-sm);
        margin-block: 0;
      }

      .dates {
        font-weight: var(--yb-text-light);
        font-size: var(--yb-text-sm);
        margin-block: 0;
      }

      .search-label {
        font-size: var(--yb-text-sm);
        font-weight: var(--yb-text-light);
      }
    }
  }
}
</style>
